/* Reading a position off a handheld GPS and typing it in.
 *
 * Geologists on this project carry dedicated GPS units. That changes an
 * assumption baked into the app: the phone is not the best source of position
 * available, it is the most convenient one. A Garmin sitting on a tripod for
 * two minutes beats a phone in a canyon by an order of magnitude, and a
 * survey-grade unit beats it by two.
 *
 * So typed coordinates are not a degraded fallback for when GPS fails. They are
 * frequently the BETTER number, and the app has to be able to say which is
 * which — exactly as it already does for compass readings, where
 * `reading_source` exists because phone magnetometers read garbage on
 * mineralised ground.
 *
 * ── Formats ────────────────────────────────────────────────────────────────
 *
 * Accepts what the units actually display, not what is convenient to parse:
 *
 *   decimal degrees   34.53120, -112.41080     34.53120 N 112.41080 W
 *   degrees/min/sec   34°31'52.3"N 112°24'38.9"W
 *   degrees/min       34 31.872 N  112 24.648 W
 *   UTM               12S 365432 3822145       12S 365432E 3822145N
 *
 * UTM matters more than it looks. Arizona field units are commonly set to UTM
 * NAD83 Zone 12N, and a geologist reading "365432 3822145" off a screen should
 * not have to convert it in their head at the outcrop. Making them do that is
 * how a digit gets transposed.
 */

export type PositionSource = 'device_gps' | 'handheld_gps' | 'manual_entry' | 'map_estimate';

export const POSITION_SOURCE_LABEL: Record<PositionSource, string> = {
  device_gps: 'Phone GPS',
  handheld_gps: 'Handheld GPS',
  manual_entry: 'Typed in',
  map_estimate: 'Estimated from a map',
};

/** What each source is worth, said plainly. Shown next to the position. */
export const POSITION_SOURCE_NOTE: Record<PositionSource, string | null> = {
  device_gps: null,
  handheld_gps: null,
  // Not a warning — a typed number is only as good as the thing it was read
  // from, and the record should say what that was.
  manual_entry: 'Typed from another instrument. Accuracy is whatever that instrument reported.',
  map_estimate: 'Placed on a map rather than measured. Treat the position as approximate.',
};

export interface ParsedPosition {
  lat: number;
  lon: number;
  /** How it was written, for the error message and for the record. */
  format: 'decimal' | 'dms' | 'ddm' | 'utm';
}

const clean = (s: string) =>
  s
    .trim()
    .replace(/[‘’“”]/g, "'")
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ');

const inRange = (lat: number, lon: number) =>
  Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180;

/* ── UTM → geographic ────────────────────────────────────────────────────────
 *
 * WGS84 inverse transverse Mercator. NAD83 and WGS84 differ by roughly a metre
 * in Arizona, which is well inside any handheld's own error, so they are not
 * distinguished here — pretending to that precision would be false comfort.
 */
const A = 6378137.0; // WGS84 semi-major axis
const F = 1 / 298.257223563;
const K0 = 0.9996;

function utmToLatLon(zone: number, easting: number, northing: number, north: boolean) {
  const e2 = F * (2 - F);
  const e1 = (1 - Math.sqrt(1 - e2)) / (1 + Math.sqrt(1 - e2));
  const x = easting - 500_000;
  const y = north ? northing : northing - 10_000_000;

  const m = y / K0;
  const mu = m / (A * (1 - e2 / 4 - (3 * e2 ** 2) / 64 - (5 * e2 ** 3) / 256));

  const phi1 =
    mu +
    ((3 * e1) / 2 - (27 * e1 ** 3) / 32) * Math.sin(2 * mu) +
    ((21 * e1 ** 2) / 16 - (55 * e1 ** 4) / 32) * Math.sin(4 * mu) +
    ((151 * e1 ** 3) / 96) * Math.sin(6 * mu);

  const ep2 = e2 / (1 - e2);
  const c1 = ep2 * Math.cos(phi1) ** 2;
  const t1 = Math.tan(phi1) ** 2;
  const n1 = A / Math.sqrt(1 - e2 * Math.sin(phi1) ** 2);
  const r1 = (A * (1 - e2)) / (1 - e2 * Math.sin(phi1) ** 2) ** 1.5;
  const d = x / (n1 * K0);

  const lat =
    phi1 -
    ((n1 * Math.tan(phi1)) / r1) *
      ((d ** 2) / 2 -
        ((5 + 3 * t1 + 10 * c1 - 4 * c1 ** 2 - 9 * ep2) * d ** 4) / 24 +
        ((61 + 90 * t1 + 298 * c1 + 45 * t1 ** 2 - 252 * ep2 - 3 * c1 ** 2) * d ** 6) / 720);

  const lon =
    (d -
      ((1 + 2 * t1 + c1) * d ** 3) / 6 +
      ((5 - 2 * c1 + 28 * t1 - 3 * c1 ** 2 + 8 * ep2 + 24 * t1 ** 2) * d ** 5) / 120) /
    Math.cos(phi1);

  const centralMeridian = ((zone - 1) * 6 - 180 + 3) * (Math.PI / 180);
  return { lat: lat * (180 / Math.PI), lon: (centralMeridian + lon) * (180 / Math.PI) };
}

/* ── Parsing ─────────────────────────────────────────────────────────────── */

/** UTM: `12S 365432 3822145`, `12 365432E 3822145N`, `12S 365432mE 3822145mN`. */
function parseUtm(s: string): ParsedPosition | null {
  const m = s.match(
    /^(\d{1,2})\s*([C-HJ-NP-X])?\s+(\d{5,7})\s*(?:m)?\s*E?\s+(\d{6,8})\s*(?:m)?\s*N?$/i,
  );
  if (!m) return null;
  const zone = Number(m[1]);
  if (zone < 1 || zone > 60) return null;
  const band = m[2]?.toUpperCase();
  // Latitude bands N and above are northern. Absent a band, assume northern —
  // this project is in Arizona and a southern-hemisphere guess would be far
  // more wrong than a northern one.
  const north = band ? band >= 'N' : true;
  const { lat, lon } = utmToLatLon(zone, Number(m[3]), Number(m[4]), north);
  return inRange(lat, lon) ? { lat, lon, format: 'utm' } : null;
}

/** Pull a signed degree value out of one half of a pair. */
function oneAxis(part: string, isLat: boolean): number | null {
  const t = clean(part).toUpperCase();
  const hemi = t.match(/[NSEW]/)?.[0];
  const body = t.replace(/[NSEW]/g, '').replace(/[°'"″′]/g, ' ').trim();
  const nums = body.split(/[\s,]+/).filter(Boolean).map(Number);
  if (!nums.length || nums.some((n) => !Number.isFinite(n))) return null;

  const neg = nums[0] < 0;
  const [d, mm = 0, ss = 0] = nums.map(Math.abs);
  let v = d + mm / 60 + ss / 3600;

  if (hemi === 'S' || hemi === 'W') v = -v;
  else if (neg) v = -v;
  // A longitude in Arizona is negative. Someone typing "112.41080" with no
  // hemisphere and no sign means west, and silently placing the record in
  // China would be worse than guessing.
  else if (!hemi && !isLat && v > 0 && v <= 180) v = -v;

  return v;
}

/**
 * Parse a coordinate a geologist actually typed.
 *
 * Returns null rather than throwing: this runs on every keystroke in a field
 * form, and a half-typed coordinate is not an error, it is a coordinate that
 * is not finished yet.
 */
export function parseCoordinates(input: string): ParsedPosition | null {
  const s = clean(input);
  if (!s) return null;

  const utm = parseUtm(s);
  if (utm) return utm;

  // Split into two halves: on a comma, or between the two hemisphere letters.
  // Both orders, because some units print longitude first and a geologist
  // copying what is on the screen should not have to reorder it.
  let halves: string[] | null = null;
  if (s.includes(',')) halves = s.split(',');
  else {
    const m =
      s.match(/^(.*?[NS])\s*(.*?[EW])$/i) ??
      s.match(/^(.*?[EW])\s*(.*?[NS])$/i) ??
      s.match(/^([-\d.]+)\s+([-\d.]+)$/);
    if (m) halves = [m[1], m[2]];
  }
  if (!halves || halves.length !== 2) return null;

  // Accept either order — some units print longitude first.
  const looksLikeLon = /[EW]/i.test(halves[0]) && /[NS]/i.test(halves[1]);
  const [latPart, lonPart] = looksLikeLon ? [halves[1], halves[0]] : halves;

  const lat = oneAxis(latPart, true);
  const lon = oneAxis(lonPart, false);
  if (lat == null || lon == null || !inRange(lat, lon)) return null;

  // Which notation was used, decided by counting the numbers in one half:
  // one is decimal degrees, two is degrees and decimal minutes, three is
  // degrees/minutes/seconds. Recorded because the record should be able to
  // show a coordinate back the way it was written.
  const pieces = clean(latPart)
    .replace(/[NSEW]/gi, '')
    .split(/[\s°'"″′]+/)
    .filter((x) => /\d/.test(x));
  const format: ParsedPosition['format'] =
    pieces.length >= 3 ? 'dms' : pieces.length === 2 ? 'ddm' : 'decimal';

  return { lat, lon, format };
}

/**
 * Is this position plausibly on the property?
 *
 * Not validation — a geologist may legitimately record something off-claim, and
 * refusing the save would be the one unforgivable behaviour in this app. It is
 * a prompt: a transposed digit puts a record a hundred kilometres away, and
 * catching that at the moment of entry costs nothing.
 */
export function looksFarFromProperty(
  lat: number,
  lon: number,
  centre: { lat: number; lon: number },
  km = 25,
): boolean {
  const dLat = (lat - centre.lat) * 111;
  const dLon = (lon - centre.lon) * 111 * Math.cos((centre.lat * Math.PI) / 180);
  return Math.hypot(dLat, dLon) > km;
}

/* ── Does this position make sense? ──────────────────────────────────────────
 *
 * Graduated, and never blocking. A geologist may legitimately record something
 * off-claim — float in a wash downstream, a regional comparison, a road
 * outcrop on the way in — and refusing that save would be the one unforgivable
 * behaviour in this app. Everything here is a prompt, not a gate.
 *
 * The point of the check is the transposed digit. Typing 34.53 as 43.53 lands a
 * record 1,000 km away and nothing about the number looks wrong on the screen;
 * it is nine characters that all belong. Catching that at the moment of entry
 * costs one glance. Catching it two years later, in a 43-101 review, costs the
 * dataset's credibility.
 */

export type PositionVerdict = 'inside' | 'near' | 'far' | 'implausible';

export interface PositionCheck {
  verdict: PositionVerdict;
  /** Kilometres from the property, 0 when inside. */
  km: number;
  /** What to say. Null when there is nothing worth saying. */
  message: string | null;
}

/** Ray casting. Polygons here are small and simple; nothing needs a library. */
export function pointInPolygon(lon: number, lat: number, ring: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

const KM_PER_DEG = 111.32;

/** Great-circle-ish. Flat-earth is fine over the tens of kilometres involved. */
function kmBetween(aLon: number, aLat: number, bLon: number, bLat: number): number {
  const dLat = (aLat - bLat) * KM_PER_DEG;
  const dLon = (aLon - bLon) * KM_PER_DEG * Math.cos((((aLat + bLat) / 2) * Math.PI) / 180);
  return Math.hypot(dLat, dLon);
}

/** Shortest distance from a point to a polygon's edges, in km. */
function kmToRing(lon: number, lat: number, ring: [number, number][]): number {
  let best = Infinity;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [x1, y1] = ring[j];
    const [x2, y2] = ring[i];
    // Project onto the segment in degree space, then measure in km. Good to
    // well under a percent at this scale.
    const dx = x2 - x1;
    const dy = y2 - y1;
    const t = dx || dy ? Math.max(0, Math.min(1, ((lon - x1) * dx + (lat - y1) * dy) / (dx * dx + dy * dy))) : 0;
    best = Math.min(best, kmBetween(lon, lat, x1 + t * dx, y1 + t * dy));
  }
  return best;
}

/**
 * Check a position against the ground it is supposed to be on.
 *
 * `boundary` is the claim polygon when one is known. `placeholder` says the
 * polygon is invented — in which case the language has to soften, because
 * telling someone they are "outside the claim" on the strength of a made-up
 * boundary would train them to ignore the warning that matters.
 */
export function checkPosition(
  lat: number,
  lon: number,
  property: { center: [number, number]; bounds: [[number, number], [number, number]] },
  opts: { placeholder?: boolean; nearKm?: number; farKm?: number } = {},
): PositionCheck {
  const { placeholder = false, nearKm = 16, farKm = 80 } = opts;

  const [[west, south], [east, north]] = property.bounds;
  const ring: [number, number][] = [
    [west, south],
    [east, south],
    [east, north],
    [west, north],
  ];

  if (pointInPolygon(lon, lat, ring)) return { verdict: 'inside', km: 0, message: null };

  const km = kmToRing(lon, lat, ring);

  if (km <= nearKm) {
    return {
      verdict: 'near',
      km,
      message: placeholder
        ? `${km.toFixed(1)} km outside the placeholder boundary. Those extents are not real yet, so this is only a rough check.`
        : `${km.toFixed(1)} km outside the claim. Fine if that is deliberate.`,
    };
  }

  if (km <= farKm) {
    return {
      verdict: 'far',
      km,
      message: `${Math.round(km)} km from ${property.center ? 'the property' : 'the claim'}. Worth a second look at the digits before saving.`,
    };
  }

  /* Past this it is almost always a typo, and there is a specific one worth
     naming: swapping the two numbers turns 34.53/-112.41 into a point in the
     Pacific, and transposing a single digit moves a record a thousand
     kilometres. Both look completely normal on the screen. */
  return {
    verdict: 'implausible',
    km,
    message:
      `${Math.round(km)} km away — that is almost certainly a typo. Check for a transposed digit, ` +
      `or latitude and longitude the wrong way round. The record will still save if you mean it.`,
  };
}
